import  { HTMLAttributes, useContext, useState } from "react"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../common/alert-dialog"
import { DictType } from "@pma/types"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import useTranslator from "@pma/hooks/use-translator"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { RESTORE_TASK, SERVER_URL } from "@pma/utils/api-routes"
import { useAuth } from "@pma/stores/auth-store"
import { toast } from "@pma/hooks/use-toast"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "../common/button"

interface Props extends HTMLAttributes<HTMLDivElement> {
  task?: DictType
  tasks?: Array<any>
}

const RestoreTask = ({ task, children, tasks }: Props) => {
  const { translate } = useTranslator()
  const { authConfig } = useAuth()
  const navigate = useNavigate()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const queryClient = useQueryClient()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)
  const [open, setOpen] = useState(false)

  const { setSelectedTasks } = useContext(GroupedTasksContext) as {
    setSelectedTasks: (tasks: any[]) => void
  }

  const restoreTask = async () => {
    const taskIds = []
    if (task) taskIds.push(task.id)
    if (tasks) tasks.map((task) => taskIds.push(task.id))

    const response = await axios.post(
      SERVER_URL + RESTORE_TASK,
      {
        organization_id: authConfig.last_organization_id,
        tasks_id: taskIds,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )

    return response.data
  }

  const restoreTaskMutation = useMutation({
    mutationKey: ["delete-task", task?.id, tasks],
    mutationFn: restoreTask,
    onSuccess: (data) => {
      if (tasks) {
        setSelectedTasks([])
        toast({
          title: translate("Success"),
          description: translate("Selected task has been restore"),
          variant: "success",
        })
        queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
        queryClient.invalidateQueries({ queryKey: ["clear-selected-group-tasks"] })
        newParams.set("revalidate", "tasks")
        navigate(`?${newParams.toString()}`)
      } else {
        toast({
          title: translate("Success"),
          description: translate("task has been restored"),
          variant: "success",
        })
        queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
        newParams.set("revalidate", "tasks")
        navigate(`?${newParams.toString()}`)
      }
      setOpen(false)
    },
    onError: (error) => {
      setOpen(false)
      if (task) {
        toast({
          title: translate("Error"),
          description: `${translate("Unable to restored")} ${task?.title} ${translate("task")}`,
          variant: "error",
        })
      }

      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
    },
  })

  return (
    <AlertDialog open={open}>
      <AlertDialogTrigger onClick={()=> setOpen(true)}>
        <Tooltip>
          <TooltipTrigger>{children}</TooltipTrigger>
          <TooltipContent className='flex items-center gap-1 bg-destructive'>
            <h4>
              {translate(`Restore`)} {task?.title} {tasks && translate("Selected tasks")}
            </h4>
          </TooltipContent>
        </Tooltip>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to restore")} {task?.title}
            {tasks && <span className='text-red-600'>{translate("Selected tasks")}</span>}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This will restore the task and subitems")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setOpen(false)}>{translate("Cancel")}</AlertDialogCancel>
          <Button
            disabled={restoreTaskMutation?.isPending}
            loading={restoreTaskMutation?.isPending}

            onClick={() => restoreTaskMutation.mutate()}
          >
            {translate("Continue")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default RestoreTask
